//src/app/features/services/step-one/ServiceDetailsModal.tsx
import React, { useState } from 'react';
import SelectSearchDropDown from 'Components/Form/SearchDropDown/SelectSearchDropDown';
import { CustomButton } from 'Components/Auth/CustomButton';
import { successToast, errorToast } from 'Components/Notifications';
import { Service } from 'app/services/responseTypes';

interface ServiceDetailsModalProps {
  service: Service;
  onClose: () => void;
  AddCartItem: (cartItem: {
    product_id: number;
    price_id: number;
    business_id: number | undefined;
  }) => Promise<void>;
  businesses: { name: string; id: string | undefined }[];
  setViewMode: React.Dispatch<React.SetStateAction<'card' | 'categorized'>>; // Prop to restore card view
}

const ServiceDetailsModal: React.FC<ServiceDetailsModalProps> = ({
  service,
  onClose,
  AddCartItem,
  businesses,
  setViewMode,
}) => {
  const [selectedBusiness, setSelectedBusiness] = useState<{
    name: string;
    id: string | undefined;
  }>({
    name: 'Personal',
    id: undefined,
  });
  const [isLoading, setIsLoading] = useState(false);

  // Handle Add to Cart
  const handleAddToCart = async () => {
    setIsLoading(true);
    try {
      await AddCartItem({
        product_id: service.id,
        price_id: service.prices[0]?.id,
        business_id: Number(selectedBusiness.id), // Pass `0` for "Personal"
      });
      successToast('Service added to cart successfully!');
      setViewMode('card'); // Restore card view
      onClose(); // Close modal
    } catch (error: any) {
      errorToast(error?.data?.message || 'Failed to add service to cart.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center modal-overlay"
      onClick={(e) =>
        (e.target as Element).classList.contains('modal-overlay') && onClose()
      }
    >
      <div className="bg-white p-6 rounded-lg w-96 relative max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
          onClick={onClose}
        >
          ✖
        </button>
        <h3 className="text-lg font-bold mb-4">{service.title}</h3>
        <p className="text-gray-700 mb-4">
          {service.description || 'No description available.'}
        </p>
        <p className="text-gray-600">
          Price: <strong>${service.prices[0]?.amount_cents / 100 || 0}</strong>
        </p>
        <div className="my-4">
          <SelectSearchDropDown
            options={businesses}
            value={selectedBusiness}
            onChange={(e) => setSelectedBusiness(e)}
            clearable={false}
            borderInputColor="border-green"
            dropDownArrowColor="text-green"
            searchFilter={(search, options) =>
              options.filter((x) =>
                x.name.toLowerCase().includes(search.toLowerCase()),
              )
            }
            selectedItemSkin={(category) => (
              <span className="font-semibold text-green">{category.name}</span>
            )}
            optionItemSkin={(category) => category.name}
          />
        </div>
        <div className="flex justify-end gap-4 mt-6">
          <button
            className="bg-gray-300 text-gray-800 py-2 px-4 rounded hover:bg-gray-400"
            onClick={onClose}
          >
            Close
          </button>
          <CustomButton
            text={isLoading ? 'Adding...' : 'Add to Cart'}
            loading={isLoading}
            bgColor="green"
            textColor="white"
            onClick={handleAddToCart}
          />
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailsModal;
